import React from 'react';
import { Box, Button, Card, Grid, Typography } from '@mui/material';

interface SuggestedTopic {
  name: string;
  emoji: string | null;
  tasks: string[];
}

interface TopicSelectionStepProps {
  suggestions: SuggestedTopic[];
  selectedTopics: Set<string>;
  onChange: (selected: Set<string>) => void;
}

const TopicSelectionStep: React.FC<TopicSelectionStepProps> = ({ suggestions, selectedTopics, onChange }) => {
  const allSelected = suggestions.length > 0 && selectedTopics.size === suggestions.length;

  const toggleAll = () => {
    if (allSelected) {
      onChange(new Set());
    } else {
      onChange(new Set(suggestions.map((t) => t.name)));
    }
  };

  const toggleTopic = (name: string) => {
    const next = new Set(selectedTopics);
    if (next.has(name)) {
      next.delete(name);
    } else {
      next.add(name);
    }
    onChange(next);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography
          variant="h6"
          sx={{ fontFamily: '"Cormorant Garamond", serif', fontWeight: 600 }}
        >
          Choose your topics
        </Typography>
        <Button size="small" onClick={toggleAll} sx={{ textTransform: 'none' }}>
          {allSelected ? 'Deselect All' : 'Select All'}
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary">
        Select the categories relevant to your wedding. Don't worry — you can add, remove, or rename topics anytime later.
      </Typography>

      <Grid container spacing={1.5}>
        {suggestions.map((topic) => {
          const isSelected = selectedTopics.has(topic.name);
          return (
            <Grid key={topic.name} size={{ xs: 6, sm: 4, md: 3 }}>
              <Card
                onClick={() => toggleTopic(topic.name)}
                elevation={0}
                sx={{
                  cursor: 'pointer',
                  textAlign: 'center',
                  py: 2,
                  px: 1,
                  border: '2px solid',
                  borderColor: isSelected ? 'primary.main' : 'grey.200',
                  bgcolor: isSelected ? 'primary.light' : 'background.paper',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    borderColor: isSelected ? 'primary.main' : 'grey.400',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 2px 8px rgba(176,122,143,0.1)',
                  },
                }}
              >
                <Typography sx={{ fontSize: '1.8rem', mb: 0.5 }}>
                  {topic.emoji || '📋'}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: isSelected ? 600 : 500,
                    color: isSelected ? 'primary.dark' : 'text.primary',
                    fontSize: '0.8rem',
                  }}
                >
                  {topic.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {topic.tasks.length} tasks
                </Typography>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};

export default TopicSelectionStep;
